import React, { useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { EventType } from '@azure/msal-browser';
import { useMutation } from '@tanstack/react-query';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import jwtDecode from 'jwt-decode';
import { useNavigate } from 'react-router-dom';

import * as T from '^/types';
import { jsonContentHeader, makeV2APIURL } from '^/store/duck/API';
import { useL10n } from '^/hooks';
import Text from './text';
import MicrosoftSVG from '^/assets/icons/microsoft.svg';
import route from '^/constants/routes';
import { finishSignIn } from '^/store/auth';
import { ChangeAuthedUser } from '^/store/duck/Auth';
import { ChangeLanguage, ShowSignUpTutorialPopup } from '^/store/duck/Pages';
import { insertUserToSentry } from '^/store/duck/vendor/sentry';
import { APIToUser, setUserInfo } from '^/store/react-query/users';
import { GetSignInResponse } from '^/store/react-query/auth';
import { http } from '^/utilities/api';
import Button from '^/components/atoms/Button';

const MicrosoftIcon = styled(MicrosoftSVG)({
  width: '21px',
  marginRight: '15px',
});

interface Props {
  isSignUp?: boolean;
}

export default function MicrosoftButton({ isSignUp = false }: Props) {
  const [l10n] = useL10n();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const fallbackLang: T.Language = useSelector((s: T.State) => s.Pages.Common.language);
  const slug: string | undefined = useSelector((s: T.State) => s.PlanConfig.config?.slug);
  const { instance } = useMsal();
  const { mutate } = useMutation(
    async ({ idToken, payload }: { idToken: string; payload: any }) => {
      const url = makeV2APIURL('auth', 'microsoft', 'callback');
      const response = await http.post(
        url,
        { auth: payload },
        {
          headers: {
            'AD-ID-TOKEN': `Token ${idToken}`,
            ...jsonContentHeader,
          },
        }
      );
      return response.data;
    },
    {
      onSuccess({ data, meta }: Partial<GetSignInResponse>, { idToken }) {
        const { email } = jwtDecode<{ email: string }>(idToken);
        if (!data || !meta) {
          navigate(`${route.signup.main}?email=${encodeURIComponent(email)}`);
          return;
        }
        const user = APIToUser(data, slug);
        setUserInfo(user);
        const authedUser = {
          id: parseInt(data.id, 10),
          token: data.attributes.token,
          cloudFrontPolicy: meta?.cloudFrontPolicy,
          cloudFrontSignature: meta?.cloudFrontSignature,
          cloudFrontKeyPairId: meta?.cloudFrontKeyPairId,
          cloudCdnCookie: meta?.cloudCdnCookie,
          refreshToken: data?.attributes.refreshToken,
        } satisfies T.AuthState['authedUser'];
        dispatch(ChangeAuthedUser({ authedUser }));
        // setAuthedUser(authedUser);
        dispatch(
          ChangeLanguage({
            language: user.language ?? fallbackLang,
          })
        );
        // setLanguage(user.language ?? fallbackLang);
        insertUserToSentry((({ id, ...others }) => ({ id: String(id), ...others }))({ ...user }));
        finishSignIn({});
        dispatch(ShowSignUpTutorialPopup());
      },
      onError(err: any, { idToken }) {
        if (err.response.status === 401) {
          const { email } = jwtDecode<{ email: string }>(idToken);
          navigate(`${route.signup.main}?email=${encodeURIComponent(email)}`);
        }
      },
    }
  );

  const initializeSignIn = async (e: any) => {
    e.preventDefault();
    // void instance.loginRedirect();
    await instance.loginPopup({
      scopes: ['user.read'],
      prompt: 'select_account',
    });
  };

  useEffect(() => {
    const callbackId = instance.addEventCallback((event: any) => {
      if (
        (event.eventType === EventType.LOGIN_SUCCESS ||
          event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) &&
        event.payload
      ) {
        const idToken = event.payload?.idToken;
        mutate({ idToken, payload: event.payload });
      }

      if (event.eventType === EventType.LOGIN_FAILURE) {
        // Learn more about AAD error codes at https://docs.microsoft.com/en-us/azure/active-directory/develop/reference-aadsts-error-codes
        // eslint-disable-next-line no-console
        console.error('Continue with microsoft failed.');
      }
    });

    return () => {
      if (callbackId) {
        instance.removeEventCallback(callbackId);
      }
    };
  }, [instance]);

  return (
    <>
      <Button
        variant="secondary"
        size="large"
        customStyles={{ width: '100%', margin: '20px auto 0px auto' }}
        onClick={initializeSignIn}
      >
        <MicrosoftIcon /> {l10n(isSignUp ? Text.signup : Text.signin)}
      </Button>
    </>
  );
}
